import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, IonTextarea, ModalController, NavController, PopoverController } from '@ionic/angular';
import { Chart, ChartDataset, ChartOptions } from 'chart.js';
import * as _ from 'lodash';
import Markdown from 'markdown-it';
import moment from 'moment-timezone';

import { DateRangePickerService } from 'src/app/components/date-range-picker/date-range-picker.service';
import { AnalyticsAssistantMessage, AnalyticsAssistantService } from 'src/services/analytics-assistant.service';

import {
  CollaboratorType,
  CUSTOM_FIELD_TYPES,
  Customer,
  Organizer,
  Profile,
  SavedReport,
  SIMPLE_CONDITIONS_OPERATORS,
} from '../../../models';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { CollaboratorTypesService } from '../../../services/collaborator-types.service';
import { CustomFieldsService } from '../../../services/custom-fields.service';
import { MessageService } from '../../../services/message.service';
import { SearchablePopoverService } from '../../../services/searchable-popover.service';
import {
  ActionDropdownComponent,
  ActionDropdownType,
} from '../../components/action-dropdown/action-dropdown.component';
import {
  AI_CONVERSATION_ROLES,
  CHART_DATE_DIMENSIONS,
  CHART_TYPE_VALUES,
  CONVERSATION_PRIORITY_VALUES,
  CONVERSATION_STATUS,
  DATE_OPTIONS,
  DATE_RANGE_TYPES,
  GROUP_BY_OPTIONS,
  routes,
  STATUS_COLUMN_FORMAT_MAP,
  UNIT,
} from '../../constants';
import {
  reconstructAndHydrateUiFilters,
  FilterOption,
  SelectedFilterMap,
  FilterValue,
  CURRENT_USER_SENTINEL,
} from '../../utils/filter-utils';
import { getDatesFromDateRange, getRandomColor, downloadCSV as utilDownloadCsv } from '../../utils/utils';

@Component({
  selector: 'app-analytics-response-times',
  templateUrl: './analytics-response-times.component.html',
  styleUrls: ['./analytics-response-times.component.scss'],
})
export class AnalyticsResponseTimesComponent implements OnInit {
  @ViewChild('chart') chart: Chart;
  @ViewChild('assistantInputElement') assistantInputElement: IonTextarea;
  @ViewChild('assistantChatInputElement') assistantChatInputElement: IonTextarea;

  CHART_TYPES = [
    CHART_TYPE_VALUES.TOTAL_COUNT,
    CHART_TYPE_VALUES.ESCALATION_COUNT,
    CHART_TYPE_VALUES.RESPONSE_TIME_MEAN,
    CHART_TYPE_VALUES.RESPONSE_TIME_MEDIAN,
    CHART_TYPE_VALUES.RESPONSE_TIME_P95,
    CHART_TYPE_VALUES.RESPONSE_TIME_P99,
    CHART_TYPE_VALUES.RESOLUTION_TIME_MEAN,
    CHART_TYPE_VALUES.RESOLUTION_TIME_MEDIAN,
    CHART_TYPE_VALUES.RESOLUTION_TIME_P95,
    CHART_TYPE_VALUES.RESOLUTION_TIME_P99,
    CHART_TYPE_VALUES.CLOSED_COUNT,
    CHART_TYPE_VALUES.SENTIMENT_SCORE_MEAN,
    CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEAN,
    CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEDIAN,
    CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P95,
    CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P99,
    CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEAN,
    CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEDIAN,
    CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P95,
    CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P99,
    CHART_TYPE_VALUES.ACTIVE_DURATION_MEAN,
    CHART_TYPE_VALUES.ACTIVE_DURATION_MEDIAN,
    CHART_TYPE_VALUES.ACTIVE_DURATION_P95,
    CHART_TYPE_VALUES.ACTIVE_DURATION_P99,
    CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEAN,
    CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEDIAN,
    CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P95,
    CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P99,
    CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEAN,
    CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEDIAN,
    CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P95,
    CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P99,
    CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEAN,
    CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEDIAN,
    CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P95,
    CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P99,
  ];
  DIMENSION_OPTIONS = [
    {
      label: 'Overall',
      key: undefined,
      icon: 'pulse',
    },
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel' : 'Customer',
      key: 'customerId',
      icon: this.authService.tenant.accountsEnabled ? 'chatbubble' : 'business',
    },
    {
      label: 'Assignee',
      key: 'assigneeId',
      icon: 'person',
    },
    {
      label: 'Tag',
      key: 'tagId',
      icon: 'pricetag',
    },
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel Tag' : 'Customer Tag',
      key: 'customerTagId',
      icon: 'pricetag',
    },
    {
      label: 'Source',
      key: 'sourceType',
      icon: 'chatbubbles',
    },
    {
      label: 'Status Type',
      key: 'status',
      icon: 'pulse',
    },
    {
      label: 'Status',
      key: 'subStatusKey',
      icon: 'pulse',
    },
    {
      label: 'Ticket Type',
      key: 'ticketTypeId',
      icon: 'ticket',
    },
    {
      label: 'Priority',
      key: 'priority',
      icon: 'alert-circle',
    },
    {
      label: 'Project',
      key: 'projectId',
      icon: 'folder-open',
    },
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account',
          key: 'accountId',
          icon: 'business',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account Tag',
          key: 'accountTagId',
          icon: 'pricetag',
        }
      : undefined,
    {
      label: 'Submitter',
      key: 'submitterUserId',
      icon: 'person',
    },
    {
      label: 'Custom Field',
      key: 'ticketTypeFields',
      icon: 'flask',
    },
  ].filter((o) => o !== undefined);
  chartType: string;
  chartStyle: 'bar' | 'line' | 'cards' = 'line';
  dateRange = DATE_OPTIONS[1];
  dimensionKey = this.DIMENSION_OPTIONS[0].key;
  dimensionLabel = this.DIMENSION_OPTIONS[0].label;
  groupByFilter: moment.unitOfTime.Diff | 'total' = GROUP_BY_OPTIONS[1].key as moment.unitOfTime.Diff | 'total';
  groupByFilterLabel = GROUP_BY_OPTIONS[1].label;
  customerFilter: Customer;
  assigneeFilter: Profile;
  startDate: Date | null;
  endDate: Date | null;
  isDateRangeInvalid = false;
  filterCustomers: Organizer[] | Profile[] = [];
  filters: SelectedFilterMap = new Map();
  timezone: string;
  isLoading = false;
  columns = [];
  lookups = {};
  tabularData = [];
  tabularDataWithTotals = [];
  showTotalColumn = false;
  savedReports: SavedReport[] = [];
  showSaveReportModal = false;
  savedReportName = '';
  savedReportShouldShare = false;
  isSaving = false;
  savedReportId: string | null = null;
  totalValues: any[] = [];

  businessHoursOnly = true;
  chartSmoothingEnabled = true;
  shouldShowLegend = false;

  unit = UNIT.MINUTES;

  // Assistant properties
  showAssistantPane = false;
  assistantMessages: AnalyticsAssistantMessage[] = [];
  assistantInput = '';
  isAssistantThinking = false;
  // Markdown renderer for assistant messages
  markdown = new Markdown({
    breaks: true, // Converts line breaks to <br> tags
    linkify: true, // Automatically converts URLs to clickable links
  });

  filterOptions: FilterOption[] = [
    {
      label: 'Assignee',
      id: 'assigneeId',
      searchId: 'assignedToUserId',
      icon: 'person',
      type: 'select',
      searchablePopoverOptions: {
        showCurrentUserOption: true,
      },
    },
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel' : 'Customer',
      id: 'customerId',
      icon: 'business',
      type: 'select',
    },
    {
      label: 'Submitter',
      id: 'submitterUserId',
      icon: 'person',
      type: 'select',
      searchablePopoverOptions: {
        showCurrentUserOption: true,
      },
    },
    {
      label: 'Status Type',
      id: 'status',
      icon: 'pulse',
      type: 'select',
    },
    {
      label: 'Status',
      id: 'subStatusKey',
      searchId: 'subStatus.key',
      icon: 'pulse',
      type: 'select',
    },
    {
      label: 'Source',
      id: 'sourceType',
      icon: 'log-in',
      type: 'select',
      values: [
        {
          label: 'Slack',
          id: 'slack',
        },
        {
          label: 'Email',
          id: 'email',
        },
        {
          label: 'In-app',
          id: 'widget',
        },
        {
          label: 'Dashboard',
          id: 'dashboard',
        },
        {
          label: 'Microsoft Teams',
          id: 'microsoft-teams',
        },
      ],
    },
    {
      label: 'Ticket Type',
      id: 'ticketTypeId',
      icon: 'ticket',
      type: 'select',
    },
    {
      label: 'Tag',
      id: 'tagId',
      searchId: 'tags.id',
      icon: 'pricetag',
      type: 'select',
    },
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel Tag' : 'Customer Tag',
      id: 'customerTagId',
      searchId: 'customer.tags.id',
      icon: 'pricetag',
      type: 'select',
    },
    {
      label: 'Priority',
      id: 'priority',
      icon: 'alert-circle',
      type: 'select',
      values: [
        {
          label: 'Low',
          id: '3',
        },
        {
          label: 'Medium',
          id: '5',
        },
        {
          label: 'High',
          id: '7',
        },
        {
          label: 'Critical',
          id: '9',
        },
      ],
    },
    {
      label: 'Custom Field',
      id: 'ticketTypeFields',
      icon: 'flask',
      isCustomFieldGroup: true,
      type: 'variable',
    },
    !this.authService.selectedProject
      ? {
          label: 'Project',
          id: 'projectId',
          icon: 'folder-open',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account',
          id: 'accountId',
          icon: 'business',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account Tag',
          id: 'accountTagId',
          icon: 'pricetag',
          type: 'select',
        }
      : undefined,
  ].filter((o) => o !== undefined) as FilterOption[];

  collaboratorTypes: Partial<CollaboratorType>[] = [];

  public timeSeriesData: any[] = [];
  public dimensionValues: any[];
  public chartData: ChartDataset[] = [];
  public chartLabels: any[] = [];
  public chartOptions: ChartOptions = {
    responsive: true,
    animation: false,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: this.shouldShowLegend,
        position: 'bottom',
      },
    },
    scales: {
      x: {
        offset: false,
        grid: {
          display: false,
        },
      },
      y: {
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  public getRowClass = (row) => {
    return {
      'totals-row': row.isTotalsRow,
    };
  };

  constructor(
    private popoverCtrl: PopoverController,
    private modalCtrl: ModalController,
    private navController: NavController,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private collaboratorTypesService: CollaboratorTypesService,
    private searchablePopoverService: SearchablePopoverService,
    public authService: AuthService,
    private customFieldsService: CustomFieldsService,
    private dateRangePickerService: DateRangePickerService,
    private analyticsAssistantService: AnalyticsAssistantService,
  ) {}

  ngOnInit() {
    this.timezone = moment.tz.guess();
    this.chartType = this.CHART_TYPES[0];
    this.endDate = moment().subtract(1, 'day').endOf('day').toDate();
    this.startDate = moment(this.endDate).startOf('day').subtract(7, 'days').toDate();
    this.savedReportId = this.route.snapshot.paramMap.get('reportId');
    if (this.savedReportId) {
      this.runSavedReport();
      return;
    }

    const queryParamDimension = this.route.snapshot.queryParamMap.get('dimension');
    const queryParamChartType = this.route.snapshot.queryParamMap.get('chartType');
    const queryParamBusinessHoursOnly = this.route.snapshot.queryParamMap.get('businessHoursOnly');
    const queryParamGroupBy = this.route.snapshot.queryParamMap.get('groupBy');
    const queryParamDateRange = this.route.snapshot.queryParamMap.get('dateRange');
    const queryParamStartDate = this.route.snapshot.queryParamMap.get('startDate');
    const queryParamEndDate = this.route.snapshot.queryParamMap.get('endDate');
    const queryParamSavedReportId = this.route.snapshot.queryParamMap.get('savedReportId');
    const queryParamUnit = this.route.snapshot.queryParamMap.get('unit');
    const queryParamChartSmoothingEnabled = this.route.snapshot.queryParamMap.get('chartSmoothingEnabled');
    const queryParamFilters = this.route.snapshot.queryParamMap.get('filters');
    const queryParamChartStyle = this.route.snapshot.queryParamMap.get('chartStyle');

    if (queryParamSavedReportId) {
      this.savedReportId = queryParamSavedReportId;
    }

    if (queryParamUnit) {
      this.unit = Array.from(Object.values(UNIT)).find((u) => u.value === Number(queryParamUnit));
    }

    if (queryParamChartSmoothingEnabled) {
      this.chartSmoothingEnabled = queryParamChartSmoothingEnabled === 'true';
    }

    if (queryParamBusinessHoursOnly) {
      this.businessHoursOnly = queryParamBusinessHoursOnly === 'true';
    }

    if (queryParamDimension) {
      this.DIMENSION_OPTIONS.forEach((d) => {
        if (d.key === queryParamDimension) {
          this.dimensionKey = d.key;
          this.dimensionLabel = d.label;
        }
      });
    }

    if (queryParamChartType && this.CHART_TYPES.includes(queryParamChartType)) {
      this.chartType = queryParamChartType;
    }

    if (queryParamGroupBy) {
      GROUP_BY_OPTIONS.forEach((o) => {
        if (o.key === queryParamGroupBy) {
          this.groupByFilter = o.key as moment.unitOfTime.Diff;
          this.groupByFilterLabel = o.label;
        }
      });
    }

    if (queryParamDateRange) {
      let cachedDateRange;

      try {
        cachedDateRange = JSON.parse(queryParamDateRange);
      } catch (e) {
        cachedDateRange = null;
      }

      if (cachedDateRange) {
        this.dateRange = cachedDateRange;
      }

      if (cachedDateRange?.type === DATE_RANGE_TYPES.ABSOLUTE) {
        if (queryParamEndDate) {
          this.endDate = moment(queryParamEndDate).toDate();
        }

        if (queryParamStartDate) {
          this.startDate = moment(queryParamStartDate).toDate();
        }

        this.isDateRangeInvalid = this.startDate.getTime() > this.endDate.getTime();
      }
    }

    if (queryParamFilters) {
      let cachedFilters: Record<string, FilterValue[]> | null;

      try {
        cachedFilters = JSON.parse(queryParamFilters);
      } catch (e) {
        cachedFilters = null;
      }

      if (cachedFilters) {
        this.filters = new Map();

        for (const [key, values] of Object.entries(cachedFilters)) {
          const filter = this.filterOptions.find((o) => o.id === key);

          if (filter) {
            this.filters.set(filter, values);
          }
        }
      }
    }

    if (queryParamChartStyle === 'bar' || queryParamChartStyle === 'line' || queryParamChartStyle === 'cards') {
      this.chartStyle = queryParamChartStyle;
    }

    this.getReports();
    this.fetchSavedReports();
    this.loadCollaboratorTypes();
  }

  get isAnalyticsAssistantEnabled(): boolean {
    return this.analyticsAssistantService.isFeatureEnabled();
  }

  async fetchSavedReports() {
    this.savedReports = await this.apiService.getPromise('/saved-reports');
  }

  async loadCollaboratorTypes() {
    const collaboratorTypes = await this.collaboratorTypesService.list({
      order: ['name'],
    });

    this.collaboratorTypes = collaboratorTypes.data;

    // TOOD: add this back when the backend supports it
    /*
    for (const collaboratorType of collaboratorTypes.data) {
      this.filterOptions.push({
        label: collaboratorType.name,
        searchId: 'assignedToId',
        id: `collaboratorKeyMap.${collaboratorType.key}`,
        icon: 'person',
        type: 'select',
      } as any);
    }
      */
  }

  storeNavigationState(key: string, value: string) {
    const queryParams: any = {
      [key]: value,
    };

    // Track current view filters and date ranges in the query param so you can share the view directly
    // We don't subscribe to these changes to update the UI because the change detection is too slow
    return this.navController.navigateRoot([], {
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  onChartTypeChange() {
    this.getReports();
    this.storeNavigationState('chartType', this.chartType);
  }

  getChartTypeLabel(chartType: string): string {
    const labels = {
      [CHART_TYPE_VALUES.TOTAL_COUNT]: 'Conversation Created',
      [CHART_TYPE_VALUES.ESCALATION_COUNT]: 'Escalations Created',
      [CHART_TYPE_VALUES.RESPONSE_TIME_MEAN]: 'First Response Time (Avg)',
      [CHART_TYPE_VALUES.RESPONSE_TIME_MEDIAN]: 'First Response Time (Median)',
      [CHART_TYPE_VALUES.RESPONSE_TIME_P95]: 'First Response Time (P95)',
      [CHART_TYPE_VALUES.RESPONSE_TIME_P99]: 'First Response Time (P99)',
      [CHART_TYPE_VALUES.RESOLUTION_TIME_MEAN]: 'Resolution Time (Avg)',
      [CHART_TYPE_VALUES.RESOLUTION_TIME_MEDIAN]: 'Resolution Time (Median)',
      [CHART_TYPE_VALUES.RESOLUTION_TIME_P95]: 'Resolution Time (P95)',
      [CHART_TYPE_VALUES.RESOLUTION_TIME_P99]: 'Resolution Time (P99)',
      [CHART_TYPE_VALUES.CLOSED_COUNT]: 'Resolved Conversations',
      [CHART_TYPE_VALUES.SENTIMENT_SCORE_MEAN]: 'Sentiment Score',
      [CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEAN]: 'Waiting on Agent Time (Avg)',
      [CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEDIAN]: 'Waiting on Agent Time (Median)',
      [CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P95]: 'Waiting on Agent Time (P95)',
      [CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P99]: 'Waiting on Agent Time (P99)',
      [CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEAN]: 'Waiting on Customer Time (Avg)',
      [CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEDIAN]: 'Waiting on Customer Time (Median)',
      [CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P95]: 'Waiting on Customer Time (P95)',
      [CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P99]: 'Waiting on Customer Time (P99)',
      [CHART_TYPE_VALUES.ACTIVE_DURATION_MEAN]: 'Ticket Active Duration (Avg)',
      [CHART_TYPE_VALUES.ACTIVE_DURATION_MEDIAN]: 'Ticket Active Duration (Median)',
      [CHART_TYPE_VALUES.ACTIVE_DURATION_P95]: 'Ticket Active Duration (P95)',
      [CHART_TYPE_VALUES.ACTIVE_DURATION_P99]: 'Ticket Active Duration (P99)',
      [CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEAN]: 'Ticket to Escalation Time (Avg)',
      [CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEDIAN]: 'Ticket to Escalation Time (Median)',
      [CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P95]: 'Ticket to Escalation Time (P95)',
      [CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P99]: 'Ticket to Escalation Time (P99)',
      [CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEAN]: 'Escalation to Acknowledgement Time (Avg)',
      [CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEDIAN]: 'Escalation to Acknowledgement Time (Median)',
      [CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P95]: 'Escalation to Acknowledgement Time (P95)',
      [CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P99]: 'Escalation to Acknowledgement Time (P99)',
      [CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEAN]: 'Acknowledgement to Resolution Time (Avg)',
      [CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEDIAN]: 'Acknowledgement to Resolution Time (Median)',
      [CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P95]: 'Acknowledgement to Resolution Time (P95)',
      [CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P99]: 'Acknowledgement to Resolution Time (P99)',
    };

    return labels[chartType] || chartType;
  }

  isTimeBasedMetric(chartType: string): boolean {
    // Time-based metrics that should show the unit conversion option (seconds/minutes/hours/days)
    //
    // INCLUDED: All duration/time metrics (28 total)
    // - Response time metrics (4)
    // - Resolution time metrics (4)
    // - Waiting time metrics (8)
    // - Escalation time metrics (12)
    //
    // EXCLUDED: Count and score metrics (3 total)
    // - TOTAL_COUNT (Conversation Created)
    // - CLOSED_COUNT (Resolved Conversations)
    // - SENTIMENT_SCORE_MEAN (Sentiment Score)
    const timeBasedMetrics = [
      // Response time metrics
      CHART_TYPE_VALUES.RESPONSE_TIME_MEAN,
      CHART_TYPE_VALUES.RESPONSE_TIME_MEDIAN,
      CHART_TYPE_VALUES.RESPONSE_TIME_P95,
      CHART_TYPE_VALUES.RESPONSE_TIME_P99,
      // Resolution time metrics
      CHART_TYPE_VALUES.RESOLUTION_TIME_MEAN,
      CHART_TYPE_VALUES.RESOLUTION_TIME_MEDIAN,
      CHART_TYPE_VALUES.RESOLUTION_TIME_P95,
      CHART_TYPE_VALUES.RESOLUTION_TIME_P99,
      // Waiting time metrics
      CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEAN,
      CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEDIAN,
      CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P95,
      CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P99,
      CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEAN,
      CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEDIAN,
      CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P95,
      CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P99,
      // Active duration metrics
      CHART_TYPE_VALUES.ACTIVE_DURATION_MEAN,
      CHART_TYPE_VALUES.ACTIVE_DURATION_MEDIAN,
      CHART_TYPE_VALUES.ACTIVE_DURATION_P95,
      CHART_TYPE_VALUES.ACTIVE_DURATION_P99,
      // Escalation time metrics
      CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEAN,
      CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEDIAN,
      CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P95,
      CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P99,
      CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEAN,
      CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEDIAN,
      CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P95,
      CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P99,
      CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEAN,
      CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEDIAN,
      CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P95,
      CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P99,
    ];

    return timeBasedMetrics.includes(chartType);
  }

  async setDimension(dimension: { key: string | undefined; label: string }, $event: Event) {
    if (dimension.key === 'ticketTypeFields') {
      // Use the dedicated popover service method to select a custom field
      try {
        await this.searchablePopoverService.customFields({
          event: $event,
          projectId: this.authService.selectedProject?.id,
          allowedTypes: [
            CUSTOM_FIELD_TYPES.CHECKBOX,
            CUSTOM_FIELD_TYPES.SINGLE_SELECT,
            CUSTOM_FIELD_TYPES.MULTI_SELECT,
            CUSTOM_FIELD_TYPES.DATE,
            CUSTOM_FIELD_TYPES.USER_SELECT,
            CUSTOM_FIELD_TYPES.MULTI_USER_SELECT,
            CUSTOM_FIELD_TYPES.USER_GROUP_SELECT,
            CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT,
          ],
          callback: (id: string, title: string, object: any) => {
            this.dimensionKey = `${dimension.key}.${id}`;
            this.dimensionLabel = title;
            this.getReports();

            this.storeNavigationState('dimension', this.dimensionKey);
          },
        });
      } catch (error) {
        console.error('Error showing custom fields popover:', error);
        this.msgSrvc.showError('Failed to load custom fields. Please try again.');
      }
    } else {
      this.dimensionKey = dimension.key;
      this.dimensionLabel = dimension.label;
      this.getReports();

      this.storeNavigationState('dimension', dimension.key);
    }
  }

  async showDateRangeOptions() {
    this.searchablePopoverService.dateValues({
      event,
      callback: async (_id: string, _name: string, object: any) => {
        this.dateRange = object;

        this.isDateRangeInvalid =
          this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE && this.startDate.getTime() > this.endDate.getTime();

        if (this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE) {
          this.showDateRangePicker();
        } else if (!this.isDateRangeInvalid) {
          this.getReports();
          this.storeNavigationState('dateRange', JSON.stringify(this.dateRange));
        }
      },
    });
  }

  async showDateRangePicker() {
    // Open the date range picker popover with event for proper positioning
    const result = await this.dateRangePickerService.openDateRangePicker({
      ...this.dateRange,
      startDate: this.startDate,
      endDate: this.endDate,
    });

    if (result) {
      // Convert the DateRange result to the format expected by the filter-button component
      this.startDate = result.startDate;
      this.endDate = result.endDate;

      this.getReports();
      this.storeNavigationState('dateRange', JSON.stringify(this.dateRange));
    }
  }

  async showGroupByOptions() {
    const options: ActionDropdownType[] = GROUP_BY_OPTIONS.map((option) => {
      return {
        label: option.label,
        icon: this.groupByFilter === option.key ? 'checkmark-outline' : 'asdf',
      };
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          this.groupByFilter = GROUP_BY_OPTIONS[idx].key;
          this.groupByFilterLabel = GROUP_BY_OPTIONS[idx].label;

          if (this.groupByFilter === CHART_DATE_DIMENSIONS.TOTAL) {
            this.chartStyle = 'cards';
          }

          this.getReports();
          this.storeNavigationState('groupBy', this.groupByFilter);
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async showChartStyleOptions() {
    const options: ActionDropdownType[] = [
      {
        label: 'Line',
        icon: 'analytics',
      },
      {
        label: 'Bar',
        icon: 'stats-chart',
      },
      {
        label: 'Cards',
        icon: 'grid',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          if (idx === 0) {
            this.chartStyle = 'line';
          } else if (idx === 1) {
            this.chartStyle = 'bar';
          } else {
            this.chartStyle = 'cards';
          }

          this.storeNavigationState('chartStyle', this.chartStyle);
          this.getReports();
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  toggleBusinessHoursOnly() {
    this.businessHoursOnly = !this.businessHoursOnly;

    this.getReports();
    this.storeNavigationState('businessHoursOnly', this.businessHoursOnly.toString());
  }

  toggleChartSmoothing() {
    this.chartSmoothingEnabled = !this.chartSmoothingEnabled;

    this.getReports();
    this.storeNavigationState('chartSmoothingEnabled', this.chartSmoothingEnabled.toString());
  }

  toggleLegendShowing() {
    this.shouldShowLegend = !this.shouldShowLegend;
    this.chartOptions = {
      ...this.chartOptions,
      plugins: {
        ...this.chartOptions.plugins,
        legend: {
          display: this.shouldShowLegend,
          position: 'bottom',
        },
      },
    };
    // this.chartOptions.plugins.legend.display = this.shouldShowLegend;
  }

  private _prepareApiFilters(uiFilters: SelectedFilterMap): Record<string, any> {
    const apiFilters: Record<string, any> = {};
    const currentUserId = this.authService.userId;
    const userFields = ['assigneeId', 'submitterUserId'];

    for (const [filterOption, filterValues] of uiFilters.entries()) {
      // Skip if no values are selected
      if (!filterValues || filterValues.length === 0) {
        continue;
      }
      const filterKey = filterOption.id;

      // --- Check if it's related to custom fields ---
      if (filterKey === 'ticketTypeFields') {
        // Handle live filtering
        // Iterate through each specific custom field filter added under this key
        filterValues.forEach((filterValue) => {
          // Key is 'ticketTypeFields', specific field and value are inside the filterValue object
          const specificFieldId = filterValue?.id; // The ID of the custom field itself
          const selectedUIValue = filterValue?.values; // The value(s) selected in the UI for that field
          const fieldType = filterValue?.additionalFields?.type;

          if (!specificFieldId) {
            console.warn('Live custom field filter missing specific field ID', filterValue);
            return; // Skip this filterValue if ID is missing
          }

          // Construct the backend key (e.g., 'ticketTypeFields.0ad...')
          const apiFilterKey = `ticketTypeFields.${specificFieldId}`;
          let backendValues: string | string[] | boolean | null = null;

          switch (fieldType) {
            case CUSTOM_FIELD_TYPES.CHECKBOX:
              // Checkbox value should already be boolean from filter component
              backendValues = typeof selectedUIValue === 'boolean' ? selectedUIValue : null;
              if (backendValues === null) {
                console.warn('Checkbox filter value is not a boolean:', selectedUIValue);
              }
              break;
            case CUSTOM_FIELD_TYPES.DATE:
            case CUSTOM_FIELD_TYPES.TEXT:
            case CUSTOM_FIELD_TYPES.SHORT_ANSWER:
            case CUSTOM_FIELD_TYPES.LONG_ANSWER:
              // Expect a single string value
              backendValues = typeof selectedUIValue === 'string' ? selectedUIValue : null;
              if (backendValues === null && selectedUIValue !== undefined) {
                console.warn('Expected string for filter value, received:', selectedUIValue);
              }
              break;
            case CUSTOM_FIELD_TYPES.SINGLE_SELECT:
            case CUSTOM_FIELD_TYPES.USER_SELECT:
            case CUSTOM_FIELD_TYPES.USER_GROUP_SELECT:
              // Expect a single object {id: ..., label: ...} or just the id string/number
              if (selectedUIValue !== null && typeof selectedUIValue === 'object' && selectedUIValue.id) {
                backendValues = String(selectedUIValue.id);
              } else if (typeof selectedUIValue === 'string' || typeof selectedUIValue === 'number') {
                backendValues = String(selectedUIValue);
              } else {
                console.warn('Unexpected format for single-select/user-select UI value:', selectedUIValue);
              }
              break;
            case CUSTOM_FIELD_TYPES.MULTI_SELECT:
            case CUSTOM_FIELD_TYPES.MULTI_USER_SELECT:
            case CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT:
              // Expect an array of objects or strings/numbers
              if (Array.isArray(selectedUIValue)) {
                backendValues = selectedUIValue
                  .map((item) =>
                    typeof item === 'object' && item !== null && item.id ? String(item.id) : String(item),
                  ) // Extract ID if object, otherwise use value as string
                  .filter((id) => id != null); // Filter out nulls/undefineds
              } else {
                console.warn('Expected array for multi-select UI value, received:', selectedUIValue);
                backendValues = []; // Treat as empty if not array
              }
              break;
            default:
              console.warn(`Unhandled custom field type in filter prep (live): ${fieldType}`);
              backendValues = String(selectedUIValue); // Fallback to string conversion
              break;
          }

          // --- SENTINEL REPLACEMENT (only if it's an array of strings/IDs) --- START
          if (
            currentUserId &&
            userFields.includes(apiFilterKey) &&
            Array.isArray(backendValues) &&
            backendValues.every((v) => typeof v === 'string')
          ) {
            backendValues = backendValues.map((val) => (val === CURRENT_USER_SENTINEL ? currentUserId : val));
          }
          // --- SENTINEL REPLACEMENT --- END

          // Assign to apiFilters if valid value was determined
          // Handle boolean separately as it shouldn't be wrapped in array
          if (typeof backendValues === 'boolean') {
            apiFilters[apiFilterKey] = backendValues;
          } else if (backendValues !== null && !(Array.isArray(backendValues) && backendValues.length === 0)) {
            // Wrap single string values in array for backend consistency (unless boolean)
            apiFilters[apiFilterKey] = Array.isArray(backendValues) ? backendValues : [backendValues];
          } else {
            console.log(`No valid backend value determined for filter ${apiFilterKey}, skipping.`);
          }
        });
      } else if (filterKey?.startsWith('ticketTypeFields.')) {
        // --- Scenario 2: Handle saved filters ---
        const apiFilterKey = filterKey; // e.g., "ticketTypeFields.0ad..."

        // Extract raw values, flatten potential arrays within fv.values, filter nulls
        // Preserve the original type of the value(s) as hydrated.
        const rawValues = filterValues
          .flatMap((fv) => {
            const valueOrValues = fv.values; // Could be single primitive or array of primitives
            return valueOrValues == null ? [] : Array.isArray(valueOrValues) ? valueOrValues : [valueOrValues];
          })
          .filter((val) => val != null);

        let backendValue: string[] | boolean | null = null;

        if (rawValues.length > 0) {
          const firstValue = rawValues[0]; // Use first value to infer structure

          // Handle Boolean Primitive
          if (typeof firstValue === 'boolean') {
            // Send direct boolean for checkbox types
            backendValue = firstValue;
          }
          // Handle Objects (Likely User/External Select)
          // Check if the raw values appear to be objects with an 'id' property
          else if (typeof firstValue === 'object' && firstValue !== null && 'id' in firstValue) {
            // Extract the 'id' from each object and convert to string for the backend array
            backendValue = rawValues
              .map((item) => (typeof item === 'object' && item !== null && 'id' in item ? item.id : null)) // Safely extract id
              .filter((id) => id != null) // Filter out nulls if any item wasn't a valid object
              .map(String); // Convert valid extracted ids to strings
          }
          // Handle Other Primitives (string, number, date strings, simple select values etc.)
          else {
            // Convert all other raw values (primitives) to an array of strings.
            // Suitable for backend operators like '?|' or potential '=' comparisons on strings/UUIDs/numbers
            backendValue = rawValues.map(String);
          }
        }

        // --- SENTINEL REPLACEMENT (only if it's an array of strings/IDs) --- START
        if (
          currentUserId &&
          userFields.includes(apiFilterKey) &&
          Array.isArray(backendValue) &&
          backendValue.every((v) => typeof v === 'string')
        ) {
          backendValue = backendValue.map((val) => (val === CURRENT_USER_SENTINEL ? currentUserId : val));
        }
        // --- SENTINEL REPLACEMENT --- END

        // Assign to apiFilters if a valid value was derived
        if (backendValue !== null && !(Array.isArray(backendValue) && backendValue.length === 0)) {
          apiFilters[apiFilterKey] = backendValue;
        } else {
          // Log if no valid values were processed (e.g., input was [null])
          console.warn(`No valid values to process for saved custom field filter: ${apiFilterKey}`);
        }
      } else {
        // --- Standard Filter Preparation ---
        let valuesToSend = filterValues.map((value) => value.id).filter((id) => id != null);

        // --- SENTINEL REPLACEMENT (only if it's an array of strings/IDs) --- START
        if (
          currentUserId &&
          userFields.includes(filterKey) &&
          Array.isArray(valuesToSend) &&
          valuesToSend.every((v) => typeof v === 'string')
        ) {
          valuesToSend = valuesToSend.map((val) => (val === CURRENT_USER_SENTINEL ? currentUserId : val));
        }
        // --- SENTINEL REPLACEMENT --- END

        // Backend expects an array of IDs for standard filters
        apiFilters[filterKey] = valuesToSend;

        // If after filtering nulls and potentially replacing sentinel, the array is empty, don't send the filter
        if (apiFilters[filterKey].length === 0) {
          delete apiFilters[filterKey];
        }
      }
    }

    // Add project filter separately if needed
    if (this.authService.selectedProject) {
      apiFilters['projectId'] = [this.authService.selectedProject.id];
    }

    return apiFilters;
  }

  getReportingQuery() {
    const params: any = {
      metric: this.chartType,
      timezone: this.timezone,
      filters: {},
      dateDimension: this.groupByFilter,
    };

    if (this.businessHoursOnly) {
      if (this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_MEAN) {
        params.metric = CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEAN;
      } else if (this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_MEAN) {
        params.metric = CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEAN;
      } else if (this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_MEDIAN) {
        params.metric = CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEDIAN;
      } else if (this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_MEDIAN) {
        params.metric = CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEDIAN;
      } else if (this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEAN) {
        params.metric = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEAN;
      } else if (this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEDIAN) {
        params.metric = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEDIAN;
      } else if (this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P95) {
        params.metric = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P95;
      } else if (this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P99) {
        params.metric = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P99;
      } else if (this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEAN) {
        params.metric = CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_MEAN;
      } else if (this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEDIAN) {
        params.metric = CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_MEDIAN;
      } else if (this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P95) {
        params.metric = CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_P95;
      } else if (this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P99) {
        params.metric = CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_P99;
      } else if (this.chartType === CHART_TYPE_VALUES.ACTIVE_DURATION_MEAN) {
        params.metric = CHART_TYPE_VALUES.ACTIVE_DURATION_WORKING_MEAN;
      } else if (this.chartType === CHART_TYPE_VALUES.ACTIVE_DURATION_MEDIAN) {
        params.metric = CHART_TYPE_VALUES.ACTIVE_DURATION_WORKING_MEDIAN;
      } else if (this.chartType === CHART_TYPE_VALUES.ACTIVE_DURATION_P95) {
        params.metric = CHART_TYPE_VALUES.ACTIVE_DURATION_WORKING_P95;
      } else if (this.chartType === CHART_TYPE_VALUES.ACTIVE_DURATION_P99) {
        params.metric = CHART_TYPE_VALUES.ACTIVE_DURATION_WORKING_P99;
      } else if (this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEAN) {
        params.metric = CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_MEAN;
      } else if (this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEDIAN) {
        params.metric = CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_MEDIAN;
      } else if (this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P95) {
        params.metric = CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_P95;
      } else if (this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P99) {
        params.metric = CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_P99;
      } else if (this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEAN) {
        params.metric = CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_MEAN;
      } else if (this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEDIAN) {
        params.metric = CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_MEDIAN;
      } else if (this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P95) {
        params.metric = CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_P95;
      } else if (this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P99) {
        params.metric = CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_P99;
      } else if (this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEAN) {
        params.metric = CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_MEAN;
      } else if (this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEDIAN) {
        params.metric = CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_MEDIAN;
      } else if (this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P95) {
        params.metric = CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_P95;
      } else if (this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P99) {
        params.metric = CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_P99;
      }
    }

    if (this.dateRange.type === DATE_RANGE_TYPES.RELATIVE) {
      params.dateRange = this.dateRange;
    } else {
      params.dateRange = {
        type: DATE_RANGE_TYPES.ABSOLUTE,
        startDate: moment(this.startDate).format('YYYY-MM-DD'),
        endDate: moment(this.endDate).format('YYYY-MM-DD'),
      };
    }

    if (this.dimensionKey) {
      params.dimensions = [this.dimensionKey];
    }

    params.filters = this._prepareApiFilters(this.filters);

    return params;
  }

  async getReports() {
    this.isLoading = true;

    const params = this.getReportingQuery();
    const promises: any[] = [];

    promises.push(this.apiService.postPromise(`/reporting/time-series`, params));

    if (this.dimensionKey) {
      promises.push(
        this.apiService.postPromise(`/reporting/time-series`, {
          ...params,
          dimensions: [],
        }),
      );
    }

    let [rawData, totalsData] = await Promise.all(promises);
    this.columns = rawData.dates;
    this.lookups = rawData.lookups;
    this.timeSeriesData = rawData.data;
    this.tabularData = (this.dimensionKey ? Object.keys(this.timeSeriesData) : [undefined])
      .map((value) => {
        const dimensionDisplayData = this.getValue(value, this.dimensionKey);
        const row = {
          dimensionData: dimensionDisplayData,
          dimension: value,
          dimensionLabel: dimensionDisplayData.value,
          total: 0,
        };

        for (const date of this.columns) {
          row[date] = this.getCellValue(value, date, this.timeSeriesData);
          row.total += row[date];
        }

        return row;
      })
      .filter((row) => {
        if (this.dimensionKey === 'tagId') {
          return row.dimension !== 'null';
        }

        return true;
      });

    if (totalsData?.data) {
      const totalsRow = {
        dimensionLabel: 'Total',
        dimension: undefined,
        isTotalsRow: true,
        dimensionData: { value: 'Total' },
        total: 0,
      };

      for (const date of this.columns) {
        totalsRow[date] = this.getCellValue(undefined, date, totalsData.data);
        totalsRow.total += totalsRow[date];
      }

      this.tabularDataWithTotals = this.tabularData.concat(totalsRow);
    } else {
      this.tabularDataWithTotals = this.tabularData;
    }

    this.setTotalValuesLocally();

    // Skip chart generation if showing cards
    if (this.chartStyle === 'cards') {
      this.chartData = [];
      this.chartLabels = [];
      this.isLoading = false;
      return;
    }

    this.showTotalColumn =
      this.chartType === CHART_TYPE_VALUES.TOTAL_COUNT && this.groupByFilter !== CHART_DATE_DIMENSIONS.TOTAL;

    const labels: Set<string> = new Set();
    const values = new Map();
    const dateFormat = this.getDateFormat();

    for (const row of this.tabularData) {
      const dimension = row.dimension;

      for (const dateString of this.columns) {
        const value = row[dateString] ?? 0;

        labels.add(moment(dateString).format(dateFormat));

        if (!values.get(dimension)) {
          values.set(dimension, []);
        }

        values.get(dimension).push(value);
      }
    }

    this.chartData = [];

    for (const [dimension, v] of values.entries()) {
      const color = this.getColor(dimension);
      this.chartData.push({
        type: this.chartStyle,
        fill: true,
        data: v,
        backgroundColor: (ctx, _options) => {
          if (this.chartStyle === 'bar') {
            return color;
          }
          const grad = ctx.chart.ctx.createLinearGradient(0, 0, 0, 600);
          grad.addColorStop(0, color);
          grad.addColorStop(1, '#ffffff00');
          ctx.chart.ctx.fillStyle = grad;
          return grad;
        },
        borderColor: color,
        tension: this.chartSmoothingEnabled ? 0.3 : 0,
        label: this.getValue(dimension, this.dimensionKey).value,
      });
    }

    if (this.chartStyle === 'bar') {
      this.chartOptions.scales.x = {
        offset: true,
        grid: {
          display: false,
        },
      };
    } else {
      this.chartOptions.scales.x = {
        offset: false,
        grid: {
          display: false,
        },
      };
    }

    this.chartLabels = Array.from(labels);
    this.showTotalColumn =
      this.chartType === CHART_TYPE_VALUES.TOTAL_COUNT && this.groupByFilter !== CHART_DATE_DIMENSIONS.TOTAL;
    this.isLoading = false;
  }

  async changeUnit() {
    const units = _.values(UNIT);
    const options: ActionDropdownType[] = units.map((option) => {
      return {
        label: option.label,
        icon: this.unit.value === option.value ? 'checkmark-outline' : 'asdf',
      };
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          this.unit = units[idx];

          this.getReports();
          this.storeNavigationState('unit', this.unit.value.toString());
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }

  downloadCSV() {
    // Use this.dimensionLabel if available, otherwise default to 'dimension'
    const firstColumnLabel = this.dimensionLabel || 'dimension';

    let headers: any[];
    let data: any[];
    let label: string;

    if (this.chartStyle === 'cards') {
      // For cards, show dimension and total value
      headers = [
        { id: 'dimensionLabel', label: firstColumnLabel },
        { id: 'total', label: 'Total' },
      ];
      data = this.totalValues.map((item) => ({
        dimensionLabel: item.label,
        total: item.value,
      }));
      label = `report-${this.chartType}-cards-${moment().format('YYYY-MM-DD')}`;
    } else {
      // For time-series data, show dimension and date columns
      headers = [{ id: 'dimensionLabel', label: firstColumnLabel }, ...this.columns];
      data = this.tabularData;
      const today = moment().endOf('day').toDate();

      label = `report-${this.chartType}-`;

      if (this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE) {
        label += `${this.startDate.toISOString()}-${this.endDate.toISOString()}`;
      } else {
        label += `${_.kebabCase(this.dateRange.label.toLocaleLowerCase())}-${today.toISOString()}`;
      }
    }

    utilDownloadCsv(data, headers, label);
  }

  getYAxisLabel() {
    if (this.chartType === CHART_TYPE_VALUES.TOTAL_COUNT) {
      return 'Conversation count';
    } else if (
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_MEAN ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_P95 ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_P99 ||
      this.chartType === CHART_TYPE_VALUES.RESPONSE_TIME_MEDIAN
    ) {
      return `First response time (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_MEAN ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_P95 ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_P99 ||
      this.chartType === CHART_TYPE_VALUES.RESOLUTION_TIME_MEDIAN
    ) {
      return `Resolution time (${this.unit.label.toLowerCase()})`;
    } else if (this.chartType === CHART_TYPE_VALUES.CLOSED_COUNT) {
      return 'Resolution count';
    } else if (this.chartType === CHART_TYPE_VALUES.SENTIMENT_SCORE_MEAN) {
      return 'Sentiment score';
    } else if (
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P95 ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P99
    ) {
      return `Waiting on agent time (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P95 ||
      this.chartType === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P99
    ) {
      return `Waiting on customer time (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.ACTIVE_DURATION_MEAN ||
      this.chartType === CHART_TYPE_VALUES.ACTIVE_DURATION_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.ACTIVE_DURATION_P95 ||
      this.chartType === CHART_TYPE_VALUES.ACTIVE_DURATION_P99
    ) {
      return `Ticket active duration (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEAN ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_MEAN ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P95 ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_P95 ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P99 ||
      this.chartType === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_P99
    ) {
      return `Ticket to escalation time (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEAN ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_MEAN ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P95 ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_P95 ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P99 ||
      this.chartType === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_P99
    ) {
      return `Escalation to acknowledgement time (${this.unit.label.toLowerCase()})`;
    } else if (
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEAN ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_MEAN ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_MEDIAN ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P95 ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_P95 ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P99 ||
      this.chartType === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_P99
    ) {
      return `Acknowledgement to resolution time (${this.unit.label.toLowerCase()})`;
    }

    return 'No data';
  }

  getValue(value: any, dimensionKey: string | undefined): { value: string; icon?: string; iconClass?: string } {
    if (!dimensionKey) {
      return {
        value: this.getYAxisLabel(),
      };
    }

    const dimensionLookups = this.lookups[dimensionKey];
    const resultValue = dimensionLookups ? dimensionLookups[String(value)] ?? String(value) : String(value);

    if (dimensionKey === 'subStatusKey') {
      const parts = typeof resultValue === 'string' ? resultValue.split(':') : [null, resultValue];
      const type = parts[0];
      const name = parts.length > 1 ? parts.slice(1).join(':') : String(parts[1] ?? 'Invalid Status');

      return {
        value: name,
        icon: STATUS_COLUMN_FORMAT_MAP[type]?.icon,
        iconClass: STATUS_COLUMN_FORMAT_MAP[type]?.iconClass,
      };
    } else if (dimensionKey === 'status') {
      return {
        value: resultValue,
        icon: STATUS_COLUMN_FORMAT_MAP[value]?.icon,
        iconClass: STATUS_COLUMN_FORMAT_MAP[value]?.iconClass,
      };
    } else {
      return {
        value: resultValue,
      };
    }
  }

  getCellValue(value, column, data) {
    const rawMetric = (value != null ? data[value][column] : data[column]) ?? 0;

    return [
      CHART_TYPE_VALUES.TOTAL_COUNT,
      CHART_TYPE_VALUES.CLOSED_COUNT,
      CHART_TYPE_VALUES.SENTIMENT_SCORE_MEAN,
      CHART_TYPE_VALUES.ESCALATION_COUNT,
    ].includes(this.chartType)
      ? rawMetric
      : Math.ceil((rawMetric as number) / this.unit.value);
  }

  getColumnName(column) {
    if (this.groupByFilter === CHART_DATE_DIMENSIONS.TOTAL) {
      return 'Total';
    }

    return moment(column).format(this.getDateFormat());
  }

  getDateFormat() {
    if (this.groupByFilter === CHART_DATE_DIMENSIONS.MONTH) {
      return 'MMM YYYY';
    }

    if ((this.groupByFilter as string) === CHART_DATE_DIMENSIONS.TOTAL) {
      return ''; // No date formatting needed for totals
    }

    return 'MMM D YYYY';
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;

    this.getReports();
    this.cacheFilters();
  }

  openSaveReportModal() {
    this.showSaveReportModal = true;
  }

  onDismissSaveReportModal() {
    this.showSaveReportModal = false;
  }

  tabIsActive(report: any) {
    const isCustomField = this.dimensionKey?.startsWith('ticketTypeFields.') && report.key === 'ticketTypeFields';
    return isCustomField || this.dimensionKey === report.key;
  }

  async saveReport() {
    const reportingQuery = this.getReportingQuery();

    // Adjust boolean filters for the /saved-reports endpoint expectation
    const filtersForSave = _.cloneDeep(reportingQuery.filters);

    for (const key in filtersForSave) {
      const value = filtersForSave[key];

      filtersForSave[key] = Array.isArray(value) ? value : [value];
    }

    const result: any = await this.apiService.postPromise('/saved-reports', {
      // Use the base query but replace filters with the adjusted ones
      ...reportingQuery,
      filters: filtersForSave,
      displaySettings: {
        unitConversion: this.unit.value,
        lineSmoothing: this.chartSmoothingEnabled,
        chartStyle: this.chartStyle,
      },
      name: this.savedReportName,
      userIds: this.savedReportShouldShare ? null : undefined,
    });

    this.savedReportShouldShare = false;
    this.savedReportName = '';
    this.savedReportId = result.id;

    this.onDismissSaveReportModal();

    document.dispatchEvent(new CustomEvent('saved-report', { detail: result }));

    setTimeout(() => {
      this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.ANALYTICS}/performance/reports/${result.id}`);
    }, 300);
  }

  async saveExistingReport(updates: any) {
    const result: any = await this.apiService.putPromise(`/saved-reports/${this.savedReportId}`, updates);

    document.dispatchEvent(new CustomEvent('saved-report', { detail: result }));
  }

  savedReportCompareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }

  async loadLookups(filters?: Record<string, string[]> | null): Promise<Record<string, Record<string, string>>> {
    if (!filters || !Object.keys(filters).length) {
      return {};
    }

    return await this.apiService.postPromise('/saved-filters/get-lookup-values', filters);
  }

  cacheFilters() {
    const cachedFilters: Record<string, FilterValue[]> = {};

    for (const [key, values] of this.filters.entries()) {
      cachedFilters[key.id] = values;
    }

    this.storeNavigationState('filters', JSON.stringify(cachedFilters));
  }

  async runSavedReport() {
    if (!this.savedReports?.length) {
      await this.fetchSavedReports();
    }

    let savedReport = this.savedReports.find((r) => r.id === this.savedReportId);

    if (!savedReport) {
      return;
    }

    this.isLoading = true;

    const loadFiltersPromise = this.loadLookups(savedReport.filters);

    if (
      [
        CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEAN,
        CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEAN,
        CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEDIAN,
        CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEDIAN,
        CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEAN,
        CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEDIAN,
        CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P95,
        CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P99,
        CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_MEAN,
        CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_MEDIAN,
        CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_P95,
        CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_P99,
        CHART_TYPE_VALUES.ACTIVE_DURATION_WORKING_MEAN,
        CHART_TYPE_VALUES.ACTIVE_DURATION_WORKING_MEDIAN,
        CHART_TYPE_VALUES.ACTIVE_DURATION_WORKING_P95,
        CHART_TYPE_VALUES.ACTIVE_DURATION_WORKING_P99,
        CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_MEAN,
        CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_MEDIAN,
        CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_P95,
        CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_P99,
        CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_MEAN,
        CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_MEDIAN,
        CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_P95,
        CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_P99,
        CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_MEAN,
        CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_MEDIAN,
        CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_P95,
        CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_P99,
      ].includes(savedReport.metric)
    ) {
      this.businessHoursOnly = true;

      if (savedReport.metric === CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEAN) {
        this.chartType = CHART_TYPE_VALUES.RESPONSE_TIME_MEAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEAN) {
        this.chartType = CHART_TYPE_VALUES.RESOLUTION_TIME_MEAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESPONSE_TIME_WORKING_MEDIAN) {
        this.chartType = CHART_TYPE_VALUES.RESPONSE_TIME_MEDIAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESOLUTION_TIME_WORKING_MEDIAN) {
        this.chartType = CHART_TYPE_VALUES.RESOLUTION_TIME_MEDIAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEAN) {
        this.chartType = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_MEDIAN) {
        this.chartType = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEDIAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P95) {
        this.chartType = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P95;
      } else if (savedReport.metric === CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_WORKING_P99) {
        this.chartType = CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P99;
      } else if (savedReport.metric === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_MEAN) {
        this.chartType = CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_MEDIAN) {
        this.chartType = CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEDIAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_P95) {
        this.chartType = CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P95;
      } else if (savedReport.metric === CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_WORKING_P99) {
        this.chartType = CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P99;
      } else if (savedReport.metric === CHART_TYPE_VALUES.ACTIVE_DURATION_WORKING_MEAN) {
        this.chartType = CHART_TYPE_VALUES.ACTIVE_DURATION_MEAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.ACTIVE_DURATION_WORKING_MEDIAN) {
        this.chartType = CHART_TYPE_VALUES.ACTIVE_DURATION_MEDIAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.ACTIVE_DURATION_WORKING_P95) {
        this.chartType = CHART_TYPE_VALUES.ACTIVE_DURATION_P95;
      } else if (savedReport.metric === CHART_TYPE_VALUES.ACTIVE_DURATION_WORKING_P99) {
        this.chartType = CHART_TYPE_VALUES.ACTIVE_DURATION_P99;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESPONSE_TIME_P95) {
        this.chartType = CHART_TYPE_VALUES.RESPONSE_TIME_P95;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESPONSE_TIME_P99) {
        this.chartType = CHART_TYPE_VALUES.RESPONSE_TIME_P99;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESOLUTION_TIME_P95) {
        this.chartType = CHART_TYPE_VALUES.RESOLUTION_TIME_P95;
      } else if (savedReport.metric === CHART_TYPE_VALUES.RESOLUTION_TIME_P99) {
        this.chartType = CHART_TYPE_VALUES.RESOLUTION_TIME_P99;
      } else if (savedReport.metric === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_MEAN) {
        this.chartType = CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_MEDIAN) {
        this.chartType = CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEDIAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_P95) {
        this.chartType = CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P95;
      } else if (savedReport.metric === CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_WORKING_P99) {
        this.chartType = CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P99;
      } else if (savedReport.metric === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_MEAN) {
        this.chartType = CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_MEDIAN) {
        this.chartType = CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEDIAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_P95) {
        this.chartType = CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P95;
      } else if (savedReport.metric === CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_WORKING_P99) {
        this.chartType = CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P99;
      } else if (savedReport.metric === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_MEAN) {
        this.chartType = CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_MEDIAN) {
        this.chartType = CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEDIAN;
      } else if (savedReport.metric === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_P95) {
        this.chartType = CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P95;
      } else if (savedReport.metric === CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_WORKING_P99) {
        this.chartType = CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P99;
      }
    } else {
      this.businessHoursOnly = false;
      this.chartType = savedReport.metric;
    }

    await this.storeNavigationState('chartType', this.chartType);
    await this.storeNavigationState('businessHoursOnly', this.businessHoursOnly.toString());

    this.dimensionKey = savedReport.dimensions?.length ? savedReport.dimensions[0] : undefined;
    this.dimensionLabel = this.DIMENSION_OPTIONS.find((d) => d.key === this.dimensionKey)?.label;

    await this.storeNavigationState('dimension', this.dimensionKey);

    this.groupByFilter = savedReport.dateDimension as any;
    this.groupByFilterLabel = GROUP_BY_OPTIONS.find((o) => o.key === this.groupByFilter)?.label;

    await this.storeNavigationState('groupBy', this.groupByFilter);

    this.dateRange = DATE_OPTIONS.find((o) => {
      if (o.type === DATE_RANGE_TYPES.ABSOLUTE && savedReport.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE) {
        return true;
      }

      if (o.type === DATE_RANGE_TYPES.RELATIVE && savedReport.dateRange.type === DATE_RANGE_TYPES.RELATIVE) {
        return (
          o.period === (savedReport.dateRange as any).period &&
          o.startIndex === (savedReport.dateRange as any).startIndex &&
          o.endIndex === (savedReport.dateRange as any).endIndex
        );
      }

      return false;
    });

    if (this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE) {
      this.startDate = moment((savedReport.dateRange as any).startDate).toDate();
      this.endDate = moment((savedReport.dateRange as any).endDate).toDate();
    }

    await this.storeNavigationState('dateRange', JSON.stringify(this.dateRange));
    await this.storeNavigationState('startDate', moment(this.startDate).format('YYYY-MM-DD'));
    await this.storeNavigationState('endDate', moment(this.endDate).format('YYYY-MM-DD'));

    if (savedReport.displaySettings.unitConversion) {
      this.unit = Array.from(Object.values(UNIT)).find((u) => u.value === savedReport.displaySettings.unitConversion);
    }

    await this.storeNavigationState('unit', this.unit.value.toString());

    if (savedReport.displaySettings.lineSmoothing != null) {
      this.chartSmoothingEnabled = savedReport.displaySettings.lineSmoothing;
    }

    await this.storeNavigationState('chartSmoothingEnabled', this.chartSmoothingEnabled.toString());

    if (
      savedReport.displaySettings.chartStyle === 'bar' ||
      savedReport.displaySettings.chartStyle === 'line' ||
      savedReport.displaySettings.chartStyle === 'cards'
    ) {
      this.chartStyle = savedReport.displaySettings.chartStyle;
    }

    await this.storeNavigationState('chartStyle', this.chartStyle);

    const filterLookups = await loadFiltersPromise;

    this.filters = await reconstructAndHydrateUiFilters(
      Object.entries(savedReport.filters).reduce((acc, [key, values]) => {
        acc.push({
          key,
          operator: SIMPLE_CONDITIONS_OPERATORS.IN,
          values: Array.isArray(values) ? values : [values],
        });

        return acc;
      }, []),
      filterLookups,
      this.filterOptions,
      this.apiService,
      this.customFieldsService,
    );

    await this.cacheFilters();

    this.savedReportId = savedReport.id;

    await this.storeNavigationState('savedReportId', this.savedReportId);

    this.isLoading = false;

    this.getReports();
  }

  async deleteSavedReport(savedReportId: string) {
    await this.apiService.deletePromise(`/saved-reports/${savedReportId}`);
    document.dispatchEvent(new CustomEvent('saved-report', { detail: savedReportId }));

    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.ANALYTICS}/performance`);
  }

  async showSavedReportOptions(savedReportId: string) {
    const savedReport = this.getSavedReport(savedReportId);

    if (!savedReport) {
      return;
    }

    const ACTION_UPDATE_FILTERS = 'Save filters to existing report';
    const ACTION_CREATE_NEW_SAVED_REPORT = 'Save as new saved report';
    const ACTION_RENAME_SAVED_REPORT = 'Rename';
    const ACTION_REMOVE_SAVED_REPORT = 'Remove saved report';

    const options: ActionDropdownType[] = [
      {
        icon: 'save-outline',
        label: ACTION_UPDATE_FILTERS,
      },
      {
        icon: 'create-outline',
        label: ACTION_RENAME_SAVED_REPORT,
      },
      {
        icon: 'add-circle-outline',
        label: ACTION_CREATE_NEW_SAVED_REPORT,
      },
      {
        icon: 'trash-outline',
        label: ACTION_REMOVE_SAVED_REPORT,
        destructive: true,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (_idx: number, label: string) => {
          popover.dismiss();

          if (label === ACTION_UPDATE_FILTERS) {
            this.saveExistingReport({
              ...this.getReportingQuery(),
              displaySettings: {
                unitConversion: this.unit.value,
                lineSmoothing: this.chartSmoothingEnabled,
                chartStyle: this.chartStyle,
              },
            });
          }
          if (label === ACTION_CREATE_NEW_SAVED_REPORT) {
            this.openSaveReportModal();
          }
          if (label === ACTION_RENAME_SAVED_REPORT) {
            const confirm = await this.alertCtrl.create({
              header: `Rename Saved Report`,
              inputs: [
                {
                  placeholder: 'Enter saved report name',
                  attributes: {
                    maxLength: 320,
                  },
                  value: savedReport.name,
                },
              ],
              buttons: [
                {
                  text: 'Cancel',
                  handler: () => {},
                },
                {
                  text: 'Save',
                  handler: async (data: any[]) => {
                    let name = data[0];

                    if (!name) {
                      return;
                    }

                    savedReport.name = name;

                    this.savedReports = [
                      ...this.savedReports.map((r) => ({
                        ...r,
                      })),
                    ];

                    this.saveExistingReport({ name });
                  },
                },
              ],
            });

            confirm.present();
          } else if (label === ACTION_REMOVE_SAVED_REPORT) {
            const confirm = await this.alertCtrl.create({
              header: `Delete Saved Report`,
              message: 'Are you sure you want to delete this saved report?',
              buttons: [
                {
                  text: 'Cancel',
                  handler: () => {},
                },
                {
                  text: 'Confirm',
                  handler: async () => {
                    await this.deleteSavedReport(savedReportId);
                  },
                },
              ],
            });

            confirm.present();
          }
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }

  getSavedReport(savedReportId: string) {
    return this.savedReports.find((r) => r.id === savedReportId);
  }

  getColor(name: string) {
    const map = {
      [CONVERSATION_STATUS.OPEN]: '#cc0022aa',
      [CONVERSATION_STATUS.IN_PROGRESS]: '#cc9922aa',
      [CONVERSATION_STATUS.ON_HOLD]: '#0000ffaa',
      [CONVERSATION_STATUS.CLOSED]: '#00cc22aa',
      [CONVERSATION_PRIORITY_VALUES.LOW]: '#00cc22aa',
      [CONVERSATION_PRIORITY_VALUES.MEDIUM]: '#0000ccaa',
      [CONVERSATION_PRIORITY_VALUES.HIGH]: '#cc9922aa',
      [CONVERSATION_PRIORITY_VALUES.CRITICAL]: '#cc0022aa',
      null: '#88888855',
    };

    return map[name] ?? getRandomColor();
  }

  setTotalValuesLocally() {
    if (!this.tabularDataWithTotals.length) {
      return [];
    }

    this.totalValues = this.tabularDataWithTotals.map((row) => ({
      label: row.dimensionLabel,
      value: row.total,
      dimensionData: row.dimensionData,
      isTotalsRow: row.isTotalsRow,
    }));
  }

  viewInInbox() {
    // Hacky way to figure out start and end of relative dates
    let startDate: Date;
    let endDate: Date;

    if (this.dateRange.type === 'absolute') {
      startDate = this.startDate;
      endDate = this.endDate;
    } else {
      [startDate, endDate] = getDatesFromDateRange(this.dateRange);
    }

    const filterMap = this.filters;
    filterMap.set(
      {
        icon: 'calendar',
        id: 'createdAt',
        label: 'Date submitted',
        type: 'date',
      },
      [
        { id: startDate.toISOString(), label: moment(startDate).format('MMM Do YYYY') },
        { id: endDate.toISOString(), label: moment(endDate).format('MMM Do YYYY') },
      ],
    );

    // Handle weird case where tagId is used in reports but tags.id is used in the inbox
    for (const [key, values] of filterMap.entries()) {
      if (key.id === 'tagId') {
        key.id = 'tags.id';
      } else if (key.id === 'customerTagId') {
        key.id = 'customer.tags.id';
      } else if (key.id === 'accountTagId') {
        key.id = 'account.tags.id';
      }
    }

    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/all`, {
      queryParams: {
        filters: JSON.stringify([...filterMap]),
      },
      queryParamsHandling: 'merge',
    });
  }

  copyLink() {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        this.msgSrvc.show('✓ Copied link to clipboard');
      })
      .catch((err) => {
        console.error('Could not copy text', err);
      });
  }

  async selectReportType() {
    const statisticalTypeOptions: ActionDropdownType[] = [
      {
        label: 'Average',
      },
      {
        label: 'Median',
      },
      {
        label: 'P95',
      },
      {
        label: 'P99',
      },
    ];

    const options: ActionDropdownType[] = [
      {
        label: this.getChartTypeLabel(CHART_TYPE_VALUES.TOTAL_COUNT),
        metadata: {
          chartType: CHART_TYPE_VALUES.TOTAL_COUNT,
        },
      },
      {
        label: 'First Response Time',
        subOptions: statisticalTypeOptions,
        metadata: {
          chartTypes: [
            CHART_TYPE_VALUES.RESPONSE_TIME_MEAN,
            CHART_TYPE_VALUES.RESPONSE_TIME_MEDIAN,
            CHART_TYPE_VALUES.RESPONSE_TIME_P95,
            CHART_TYPE_VALUES.RESPONSE_TIME_P99,
          ],
        },
      },
      {
        label: 'Resolution Time',
        subOptions: statisticalTypeOptions,
        metadata: {
          chartTypes: [
            CHART_TYPE_VALUES.RESOLUTION_TIME_MEAN,
            CHART_TYPE_VALUES.RESOLUTION_TIME_MEDIAN,
            CHART_TYPE_VALUES.RESOLUTION_TIME_P95,
            CHART_TYPE_VALUES.RESOLUTION_TIME_P99,
          ],
        },
      },
      {
        label: 'Ticket Active Time',
        subOptions: statisticalTypeOptions,
        metadata: {
          chartTypes: [
            CHART_TYPE_VALUES.ACTIVE_DURATION_MEAN,
            CHART_TYPE_VALUES.ACTIVE_DURATION_MEDIAN,
            CHART_TYPE_VALUES.ACTIVE_DURATION_P95,
            CHART_TYPE_VALUES.ACTIVE_DURATION_P99,
          ],
        },
      },
      {
        label: 'Waiting on Agent Time',
        subOptions: statisticalTypeOptions,
        metadata: {
          chartTypes: [
            CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEAN,
            CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_MEDIAN,
            CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P95,
            CHART_TYPE_VALUES.WAITING_ON_AGENT_TIME_P99,
          ],
        },
      },
      {
        label: 'Waiting on Customer Time',
        subOptions: statisticalTypeOptions,
        metadata: {
          chartTypes: [
            CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEAN,
            CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_MEDIAN,
            CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P95,
            CHART_TYPE_VALUES.WAITING_ON_CUSTOMER_TIME_P99,
          ],
        },
      },
      {
        label: 'Resolved Conversations',
        metadata: {
          chartType: CHART_TYPE_VALUES.CLOSED_COUNT,
        },
      },
      {
        label: 'Sentiment Score',
        metadata: {
          chartType: CHART_TYPE_VALUES.SENTIMENT_SCORE_MEAN,
        },
      },
      {
        label: 'Escalations Created',
        metadata: {
          chartType: CHART_TYPE_VALUES.ESCALATION_COUNT,
        },
      },
      {
        label: 'Ticket to Escalation Time',
        subOptions: statisticalTypeOptions,
        metadata: {
          chartTypes: [
            CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEAN,
            CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_MEDIAN,
            CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P95,
            CHART_TYPE_VALUES.TICKET_TO_ESCALATION_DURATION_P99,
          ],
        },
      },
      {
        label: 'Escalation to Acknowledgement Time',
        subOptions: statisticalTypeOptions,
        metadata: {
          chartTypes: [
            CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEAN,
            CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_MEDIAN,
            CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P95,
            CHART_TYPE_VALUES.ESCALATION_TO_ACKNOWLEDGEMENT_DURATION_P99,
          ],
        },
      },
      {
        label: 'Acknowledgement to Resolution Time',
        subOptions: statisticalTypeOptions,
        metadata: {
          chartTypes: [
            CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEAN,
            CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_MEDIAN,
            CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P95,
            CHART_TYPE_VALUES.ACKNOWLEDGEMENT_TO_RESOLUTION_DURATION_P99,
          ],
        },
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (_idx: number, _label: string, _id?: string, metadata?: any) => {
          const subOption = metadata.subOption;
          if (subOption) {
            this.chartType = metadata.chartTypes[subOption.index];
          } else {
            this.chartType = metadata.chartType;
          }

          this.onChartTypeChange();
          popover.dismiss();
        },
      },
      showBackdrop: false,
      event: event,
    });

    popover.present();
  }

  toggleAnalyticsAssistantPaneShowing() {
    this.showAssistantPane = !this.showAssistantPane;

    if (this.showAssistantPane) {
      this.assistantInputElement.setFocus();
    }
  }

  clearAssistantChat() {
    this.assistantMessages = [];
    this.assistantInput = '';
    this.isAssistantThinking = false;
    this.assistantInputElement.setFocus();
  }

  async sendAssistantMessage() {
    if (!this.assistantInput?.trim() || this.isAssistantThinking) return;

    // Check if feature is enabled
    if (!this.isAnalyticsAssistantEnabled) {
      this.msgSrvc.showError('Analytics assistant feature is not available');
      return;
    }

    // Add user message
    const userMessage: AnalyticsAssistantMessage = {
      role: AI_CONVERSATION_ROLES.USER,
      content: this.assistantInput.trim(),
    };
    this.assistantMessages.push(userMessage);

    this.assistantInput = '';
    this.isAssistantThinking = true;

    try {
      const response = await this.analyticsAssistantService.sendMessage({
        history: this.assistantMessages,
        currentAnalyticsContext: this.getReportingQuery(),
        timeSeriesData: this.timeSeriesData, // Pass the chart data the user is currently viewing
      });

      // Add assistant response(s)
      response.forEach((message) => {
        if (message.role === AI_CONVERSATION_ROLES.ASSISTANT) {
          this.assistantMessages.push(message);
        }
      });
      this.scrollToBottom();
    } catch (err) {
      console.error('Error calling analytics assistant:', err);
      // Fallback message
      this.assistantMessages.push({
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
      });
      this.msgSrvc.showError('Failed to get response from analytics assistant');
      this.scrollToBottom();
    } finally {
      this.isAssistantThinking = false;
      this.assistantChatInputElement.setFocus();
    }
  }

  async onAssistantEnterKey(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendAssistantMessage();
    }
  }

  convertToMarkdown(content: string): string {
    return this.markdown.render(content);
  }

  // Scroll to bottom when new messages arrive
  private scrollToBottom() {
    setTimeout(() => {
      const messagesContainer = document.querySelector('.assistant-messages');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 100);
  }
}
